import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getToday } from '../../shared/api/today'
import { addMedia, deleteMedia, listMedia, type AddMediaInput } from '../../shared/api/media'
import {
  addDocument,
  deleteDocument,
  listDocuments,
  type AddDocumentInput,
} from '../../shared/api/documents'
import { addPoint, updatePoint } from '../../shared/api/points'
import { pointKeys } from '../timeline/usePoints'
import type { CreatePointInput, UpdatePointInput } from '../../shared/api/types'

export const todayKeys = {
  byTrip: (tripId: string) => ['today', tripId] as const,
}
export const mediaKeys = {
  byTrip: (tripId: string, pointId?: string) => ['media', tripId, pointId ?? 'all'] as const,
}
export const documentKeys = {
  byTrip: (tripId: string, pointId?: string) => ['documents', tripId, pointId ?? 'all'] as const,
}

export function useToday(tripId: string | undefined) {
  return useQuery({
    queryKey: todayKeys.byTrip(tripId ?? 'unknown'),
    queryFn: () => getToday(tripId as string),
    enabled: Boolean(tripId),
  })
}

export function useMedia(tripId: string | undefined, pointId?: string) {
  return useQuery({
    queryKey: mediaKeys.byTrip(tripId ?? 'unknown', pointId),
    queryFn: () => listMedia(tripId as string, pointId),
    enabled: Boolean(tripId),
  })
}

export function useDocuments(tripId: string | undefined, pointId?: string) {
  return useQuery({
    queryKey: documentKeys.byTrip(tripId ?? 'unknown', pointId),
    queryFn: () => listDocuments(tripId as string, pointId),
    enabled: Boolean(tripId),
  })
}

/** Patch a point (mark visited, append a note) and refresh Today + points. */
export function usePatchPoint(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pointId, patch }: { pointId: string; patch: UpdatePointInput }) =>
      updatePoint(tripId, pointId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: todayKeys.byTrip(tripId) })
      qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) })
    },
  })
}

export function useAddMedia(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AddMediaInput) => addMedia(tripId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media', tripId] })
      qc.invalidateQueries({ queryKey: todayKeys.byTrip(tripId) })
    },
  })
}

export function useDeleteMedia(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (mediaId: string) => deleteMedia(tripId, mediaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media', tripId] }),
  })
}

export function useAddDocument(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AddDocumentInput) => addDocument(tripId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', tripId] })
      qc.invalidateQueries({ queryKey: todayKeys.byTrip(tripId) })
    },
  })
}

export function useDeleteDocument(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(tripId, documentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documents', tripId] })
      qc.invalidateQueries({ queryKey: todayKeys.byTrip(tripId) })
    },
  })
}

/** Add a point and refresh Today + timeline lists (Live Mode quick-add). */
export function useAddPointLive(tripId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePointInput) => addPoint(tripId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pointKeys.byTrip(tripId) })
      qc.invalidateQueries({ queryKey: todayKeys.byTrip(tripId) })
    },
  })
}
